import styled from '@emotion/styled';
import dayjs from 'dayjs';
import { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { NavLink } from 'react-router-dom';

import emptyPlayListImage from '../../../assets/empty-playlist.svg';
import { LocaleKeys } from '../../../locale';
import { useAuthSelector } from '../../../state/auth/selectors';
import { TListedPlaylist } from '../../../types/responses/video';
import { useMeSelector } from '../../../state/me/selectors';
import {
  getPlayCourseAdminPath,
  getPlayCoursePath,
  getPlaylistItemPath,
  getStudentPlaylistPath,
} from '../../../utils/paths';
import { Preview, PreviewListedItem } from './playlist-item';
import {
  VideoColDescription,
  VideoColStatItem,
  VideoColStatText,
  VideoColStatTitle,
  VideoColStatWrapper,
  VideoColText,
  VideoColTextWrapper,
  VideoGridContentWrapper,
} from './video-col-item';

type TProps = {
  openAsCourse?: boolean;
  playListData: TListedPlaylist;
};

const WrapperLink = styled(NavLink)`
  position: relative;
`;

export const PlaylistColItem: FC<TProps> = ({
  openAsCourse = false,
  playListData,
}) => {
  const { t } = useTranslation([LocaleKeys.VIDEO]);
  const { meData } = useMeSelector();
  const { data: authData } = useAuthSelector();
  const isStudentSession =
    meData?.role === 0 || !!authData?.account.studentMode;
  const parameterizedItems = playListData.items ?? [];
  const previewUrls = parameterizedItems
    .filter((item) => item.type === 'video' && item.previewUrl)
    .map((item) => item.previewUrl as string);
  const contentCount =
    parameterizedItems.length || playListData.videos?.length || 0;
  return (
    <WrapperLink
      to={
        isStudentSession && openAsCourse
          ? getPlayCoursePath(playListData.id)
          : isStudentSession
          ? getStudentPlaylistPath(playListData.id)
          : openAsCourse
            ? getPlayCourseAdminPath(playListData.id)
            : getPlaylistItemPath(playListData.id)
      }
    >
      <VideoGridContentWrapper>
        <Preview
          className={
            previewUrls.length > 1 ||
            [...(playListData.videos ?? [])].filter((el) => !!el.videoPreview)
              .length > 1
              ? 'empty'
              : undefined
          }
        >
          {previewUrls.length > 0 ? (
            previewUrls.slice(0, 2).map((previewUrl) => (
              <PreviewListedItem key={previewUrl}>
                <img
                  src={previewUrl}
                  className={'image'}
                  alt={'Превью плейлиста'}
                />
              </PreviewListedItem>
            ))
          ) : playListData.videos &&
            [...playListData.videos].filter((el) => !!el.videoPreview).length >=
              1 ? (
            [...playListData.videos]
              .filter((el) => !!el.videoPreview)
              .splice(0, 2)
              .map((video) => (
                <PreviewListedItem key={video.id}>
                  <img
                    src={video.videoPreview?.url}
                    className={'image'}
                    alt={'Превью плейлиста'}
                  />
                </PreviewListedItem>
              ))
          ) : (
            <img
              className={'empty'}
              src={emptyPlayListImage}
              alt="Плейлист без обложки"
            />
          )}
        </Preview>
        <VideoColTextWrapper>
          <VideoColText level={3}>{playListData.title}</VideoColText>
          <VideoColDescription>
            {contentCount}{' '}
            {parameterizedItems.length ? 'items' : t('playlist.video')}
          </VideoColDescription>
        </VideoColTextWrapper>
        <VideoColStatWrapper>
          <VideoColStatItem>
            <VideoColStatTitle>
              {dayjs(playListData.createdAt).format('DD MMMM HH:mm')}
            </VideoColStatTitle>
            <VideoColStatText>{t('stats.created')}</VideoColStatText>
          </VideoColStatItem>
          <VideoColStatItem>
            <VideoColStatTitle>
              {dayjs(playListData.updatedAt).format('DD MMMM HH:mm')}
            </VideoColStatTitle>
            <VideoColStatText>{t('stats.updated')}</VideoColStatText>
          </VideoColStatItem>
        </VideoColStatWrapper>
      </VideoGridContentWrapper>
    </WrapperLink>
  );
};
